/**
 * Scheduled Calls page — clients (or admins via impersonation) create and
 * manage scheduled Run dispatches. Two modes: one-time and weekly. Admin
 * gate (`schedulingEnabled`) hides the form when false; the dashboard's
 * "Schedule Calls" button instead opens a Request Access modal.
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { functions, db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useEffectiveClientId } from '../../hooks/useImpersonation';
import { useCredits } from '../../hooks/useCredits';
import { PortalHeader } from '../Layout/PortalHeader';
import { UPLOAD_LIST_WAIT_MS, uploadListErrorMessage } from './sendOutcome';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Mirrors MAX_SOURCE_FILES_PER_SCHEDULE on the server / Firestore `in` cap.
const MAX_FILES_PER_SCHEDULE = 30;

interface ScheduleRow {
  id: string;
  mode: 'one_time' | 'weekly';
  daysOfWeek: number[];
  oneTimeDate: string | null;
  timeOfDayCST: string;
  runNumber: number;
  count: number;
  // Optional file scope — when set, fires only call leads from these uploads.
  // Field is omitted on docs created before this feature shipped.
  sourceFiles?: string[];
  enabled: boolean;
  nextFireAt: { seconds: number } | null;
  lastFiredAt: { seconds: number } | null;
  fireHistory: Array<{ firedAt: { seconds: number }; sent: number; result: string }>;
}

interface FileOption {
  sourceFile: string;
  availableInRun: number;
  totalInRun: number;
  uploadedAt: number | null;
}

function formatNext(t: { seconds: number } | null): string {
  if (!t) return '—';
  return new Date(t.seconds * 1000).toLocaleString('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export function ScheduleCalls() {
  const { user } = useAuth();
  const effectiveClientId = useEffectiveClientId();
  // useCredits subscribes to clients/{effectiveClientId} via onSnapshot, so
  // it tracks impersonation correctly and keeps the displayed balance in
  // sync with credit purchases / consumption that happen while the page is
  // open. activeBalance flips between per-call and per-contact based on
  // the client's billingType — the previous one-shot getDoc(...) only read
  // `data.credits` (per-call) and showed 0 for per-contact clients.
  const { billingType, activeBalance } = useCredits();
  const navigate = useNavigate();
  const targetUid = effectiveClientId || user?.uid || '';

  // Form state
  const [mode, setMode] = useState<'one_time' | 'weekly'>('weekly');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri default
  const [oneTimeDate, setOneTimeDate] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [runNumber, setRunNumber] = useState(1);
  const [count, setCount] = useState(100);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [neededCredits, setNeededCredits] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // File-scope state (mirrors SendRunConfirmModal). 'all' = send all eligible
  // (legacy behavior, no sourceFiles persisted). 'file' = scope to picked
  // uploads; the picker is lazy-loaded when this mode is selected.
  const [scopeMode, setScopeMode] = useState<'all' | 'file'>('all');
  const [fileOptions, setFileOptions] = useState<FileOption[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [pickedFiles, setPickedFiles] = useState<Set<string>>(new Set());

  // Existing schedules + admin gate
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [schedulingEnabled, setSchedulingEnabled] = useState<boolean | null>(null);

  // Live subscription to schedules subcollection (also gives us cap visibility)
  useEffect(() => {
    if (!targetUid) return;
    const q = query(
      collection(db, 'clients', targetUid, 'schedules'),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(q, snap => {
      setSchedules(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<ScheduleRow, 'id'>) })));
    });
    return () => unsub();
  }, [targetUid]);

  // Run number changing invalidates file counts (availability is per-run),
  // so drop the cached list and any selections. Keep scopeMode where the
  // user left it — re-expanding will trigger a fresh fetch.
  useEffect(() => {
    setFileOptions(null);
    setPickedFiles(new Set());
    setFilesError(null);
  }, [runNumber]);

  // Lazy-load uploads only when the user expands the by-file panel. Same
  // single-callable approach as SendRunConfirmModal so we share a backend
  // path. Deps deliberately exclude fileOptions/filesLoading — including
  // them caused the effect to re-run mid-flight and cancel the response.
  useEffect(() => {
    if (scopeMode !== 'file' || !targetUid) return;
    if (fileOptions !== null || filesLoading) return;
    let cancelled = false;
    setFilesLoading(true);
    setFilesError(null);
    const fn = httpsCallable(functions, 'listUploadFileOptions', { timeout: UPLOAD_LIST_WAIT_MS });
    fn({ runNumber, ...(effectiveClientId ? { clientId: effectiveClientId } : {}) })
      .then(resp => {
        if (cancelled) return;
        const payload = resp.data as { files?: FileOption[] };
        setFileOptions(Array.isArray(payload.files) ? payload.files : []);
      })
      .catch(err => {
        if (cancelled) return;
        setFilesError(uploadListErrorMessage(err));
        setFileOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFilesLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeMode, targetUid, effectiveClientId, runNumber]);

  // Load client doc once for the schedulingEnabled flag. Credit balance now
  // comes from useCredits() above (live, impersonation-aware, billing-type
  // aware) — the previous getDoc here read only `credits` (per-call) and
  // showed 0 for per-contact clients and during early impersonation race.
  useEffect(() => {
    if (!targetUid) return;
    (async () => {
      const snap = await getDoc(doc(db, 'clients', targetUid));
      const d = snap.data() || {};
      setSchedulingEnabled(d.schedulingEnabled !== false);
    })();
  }, [targetUid]);

  // Pre-flight credit estimate. Per-call: 1 credit/lead. Per-contact: ~6%
  // contact rate (matches dispatcher.ts:392).
  const estimatedCostPerFire = billingType === 'per_contact'
    ? Math.ceil(count * 0.06)
    : count;
  const estimatedRecurringCost = mode === 'weekly'
    ? estimatedCostPerFire * Math.max(1, daysOfWeek.length)
    : estimatedCostPerFire;

  const toggleDay = (d: number) => {
    setDaysOfWeek(prev => {
      const s = new Set(prev);
      if (s.has(d)) s.delete(d); else s.add(d);
      return Array.from(s).sort();
    });
  };

  const togglePicked = (sf: string, hasAvailable: boolean) => {
    if (!hasAvailable) return;
    setPickedFiles(prev => {
      const next = new Set(prev);
      if (next.has(sf)) next.delete(sf);
      else if (next.size < MAX_FILES_PER_SCHEDULE) next.add(sf);
      return next;
    });
  };
  const handleSelectAllFiles = () => {
    if (!fileOptions) return;
    setPickedFiles(new Set(
      fileOptions
        .filter(f => f.availableInRun > 0)
        .slice(0, MAX_FILES_PER_SCHEDULE)
        .map(f => f.sourceFile),
    ));
  };
  const handleClearFiles = () => setPickedFiles(new Set());

  // Build the descriptor used in the consent line and the email summary.
  // Empty when sending all-eligible so the existing copy reads unchanged for
  // unsoped schedules.
  const fileScopeDescriptor = scopeMode === 'file' && pickedFiles.size > 0
    ? ` from ${pickedFiles.size} selected file${pickedFiles.size === 1 ? '' : 's'}`
    : '';

  const consentText = (() => {
    const days = mode === 'weekly'
      ? daysOfWeek.map(d => DAY_NAMES[d]).join(', ') || '(no days)'
      : oneTimeDate || '(no date)';
    return `I acknowledge this will call ${count} leads${fileScopeDescriptor} on Run ${runNumber} at ${timeOfDay} CST on ${days}, costing ~${estimatedCostPerFire} credits per fire.`;
  })();

  const tryAck = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);

    // Validation
    if (mode === 'weekly' && daysOfWeek.length === 0) {
      setResult('Error: pick at least one day of week.');
      return;
    }
    if (mode === 'one_time' && !oneTimeDate) {
      setResult('Error: pick a date.');
      return;
    }
    if (count < 100 || count > 2000) {
      setResult('Error: count must be 100–2000.');
      return;
    }
    if (scopeMode === 'file' && pickedFiles.size === 0) {
      setResult('Error: pick at least one file or switch back to “Send all eligible leads”.');
      return;
    }

    // Credit gate — compares against activeBalance (call credits for
    // per_call clients, contact credits for per_contact clients) so the
    // gate matches what the dispatcher will actually decrement at fire time.
    if (estimatedRecurringCost > activeBalance) {
      setNeededCredits(estimatedRecurringCost - activeBalance);
      setShowCreditModal(true);
      return;
    }
    setShowAckModal(true);
  };

  const submit = async () => {
    if (!targetUid) return;
    setSubmitting(true);
    setResult(null);
    setShowAckModal(false);
    try {
      const fn = httpsCallable(functions, 'createSchedule');
      const res = await fn({
        clientId: effectiveClientId,
        mode,
        daysOfWeek: mode === 'weekly' ? daysOfWeek : undefined,
        oneTimeDate: mode === 'one_time' ? oneTimeDate : undefined,
        timeOfDayCST: timeOfDay,
        runNumber,
        count,
        consentText,
        // Only send when actually scoped — keeps the doc shape clean for
        // legacy "send all eligible" schedules and avoids the server
        // having to interpret an empty array.
        ...(scopeMode === 'file' && pickedFiles.size > 0
          ? { sourceFiles: Array.from(pickedFiles) }
          : {}),
      });
      const data = res.data as { success: boolean; scheduleId: string };
      setResult(`Schedule created (${data.scheduleId}). A confirmation email will arrive shortly.`);
      setAcknowledged(false);
      // Reset the file scope so the form doesn't carry selections into the
      // next schedule the user creates.
      setScopeMode('all');
      setPickedFiles(new Set());
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSchedule = async (id: string) => {
    if (!confirm('Delete this schedule?')) return;
    try {
      const fn = httpsCallable(functions, 'deleteSchedule');
      await fn({ clientId: effectiveClientId, scheduleId: id });
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    }
  };

  const toggleEnabled = async (s: ScheduleRow) => {
    try {
      const fn = httpsCallable(functions, 'updateSchedule');
      await fn({ clientId: effectiveClientId, scheduleId: s.id, enabled: !s.enabled });
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    }
  };

  // --- Render ---

  // PortalHeader takes credit info as props (it doesn't fetch internally) —
  // without these the badge defaults to "Call Credits: 0" regardless of the
  // real balance. activeBalance/billingType come from useCredits which is
  // already impersonation-aware via useEffectiveClientId.
  const headerProps = {
    showCredits: true,
    activeBalance,
    billingType: billingType === 'per_contact' ? 'per_contact' as const : 'per_call' as const,
  };

  if (schedulingEnabled === null) {
    return <div><PortalHeader {...headerProps} /><div className="container">Loading…</div></div>;
  }

  if (!schedulingEnabled) {
    return (
      <div>
        <PortalHeader {...headerProps} />
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center', padding: '60px 20px' }}>
          <h2>Scheduled Calls is currently disabled</h2>
          <p style={{ color: 'var(--gray-400)' }}>
            Contact <a href="mailto:info@jadenterprises.org">info@jadenterprises.org</a> to request access.
          </p>
          <Link to="/" className="btn btn-secondary" style={{ marginTop: '20px' }}>← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PortalHeader {...headerProps} />
      <div className="container" style={{ maxWidth: '820px' }}>
        <h2>Schedule Calls</h2>
        <p style={{ color: 'var(--gray-400)' }}>
          Set up automatic call dispatches. Up to 3 schedules per day.<br />
          Times are in CST and apply between 8:00 AM and 7:00 PM.
        </p>

        {/* Existing schedules — hide completed one-time schedules so the list
            stays focused on what's still active. A one-time schedule with
            `lastFiredAt` set has fired and auto-disabled (see scheduledRunFirer
            in functions/orchestrator/schedules.ts), so the row is just clutter
            from the user's perspective. The Firestore doc is preserved for
            audit trail (fireHistory, consentText, etc.) — only the UI hides it.
            Weekly schedules are never auto-completed; they fire each matching
            day and stay visible until the user pauses or deletes them. */}
        {(() => {
          const visibleSchedules = schedules.filter(s =>
            !(s.mode === 'one_time' && s.lastFiredAt)
          );
          if (visibleSchedules.length === 0) return null;
          return (
          <div className="form-group" style={{ marginTop: '24px' }}>
            <label>Your schedules</label>
            <div style={{ display: 'grid', gap: '8px' }}>
              {visibleSchedules.map(s => (
                <div key={s.id} style={{
                  padding: '12px 14px', border: '1px solid var(--dark-3)', borderRadius: '8px',
                  background: 'var(--dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, color: s.enabled ? 'var(--gold)' : 'var(--gray-500)' }}>
                      {s.mode === 'weekly'
                        ? `Weekly · ${s.daysOfWeek.map(d => DAY_NAMES[d]).join(', ')}`
                        : `Once · ${s.oneTimeDate}`}
                      {' · '}{s.timeOfDayCST} CST · Run {s.runNumber} · {s.count} leads
                      {s.sourceFiles && s.sourceFiles.length > 0 && (
                        <span style={{ color: 'var(--gold)', fontSize: '0.85rem', fontWeight: 600 }}>
                          {' · '}{s.sourceFiles.length} file{s.sourceFiles.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                      Next: {formatNext(s.nextFireAt)} · Last: {formatNext(s.lastFiredAt)}
                    </div>
                    {s.sourceFiles && s.sourceFiles.length > 0 && (
                      <div
                        style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}
                        title={s.sourceFiles.join('\n')}
                      >
                        Files: {s.sourceFiles.slice(0, 3).join(', ')}
                        {s.sourceFiles.length > 3 && ` +${s.sourceFiles.length - 3} more`}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => toggleEnabled(s)}>
                      {s.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => deleteSchedule(s.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* New schedule form */}
        <form onSubmit={tryAck} style={{ marginTop: '24px' }}>
          <h3>New schedule</h3>

          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Mode *</label>
            <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                cursor: 'pointer', fontSize: '1.05rem', whiteSpace: 'nowrap',
              }}>
                <input
                  type="radio" checked={mode === 'one_time'}
                  onChange={() => setMode('one_time')}
                  style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                /> One Time
              </label>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                cursor: 'pointer', fontSize: '1.05rem', whiteSpace: 'nowrap',
              }}>
                <input
                  type="radio" checked={mode === 'weekly'}
                  onChange={() => setMode('weekly')}
                  style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                /> Weekly
              </label>
            </div>
          </div>

          {mode === 'weekly' ? (
            <div className="form-group">
              <label style={{ fontSize: '1rem' }}>Days of week *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                {DAY_NAMES.map((name, idx) => {
                  const checked = daysOfWeek.includes(idx);
                  return (
                    <button
                      key={idx} type="button" onClick={() => toggleDay(idx)}
                      style={{
                        padding: '12px 4px', borderRadius: '6px',
                        border: `2px solid ${checked ? 'var(--gold)' : 'var(--dark-3)'}`,
                        background: checked ? 'rgba(221,177,84,0.1)' : 'var(--dark)',
                        color: checked ? 'var(--gold)' : 'var(--gray-300)',
                        cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.95rem',
                      }}
                    >{name}</button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label style={{ fontSize: '1rem' }}>Date *</label>
              <input type="date" value={oneTimeDate} onChange={e => setOneTimeDate(e.target.value)}
                required style={{ fontSize: '1rem', padding: '10px 12px' }} />
            </div>
          )}

          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Time of day (8:00 AM and 7:00 PM CST) *</label>
            <input type="time" min="08:00" max="19:00" value={timeOfDay}
              onChange={e => setTimeOfDay(e.target.value)} required
              style={{ fontSize: '1rem', padding: '10px 12px' }} />
          </div>

          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Run number *</label>
            <select value={runNumber} onChange={e => setRunNumber(Number(e.target.value))}
              style={{ fontSize: '1rem', padding: '10px 12px' }}>
              {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>Run {n}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Number of leads to call (100 – 2000) *</label>
            <input type="number" min={100} max={2000} value={count}
              onChange={e => setCount(Number(e.target.value))} required
              style={{ fontSize: '1rem', padding: '10px 12px' }} />
          </div>

          {/* File scope — same UX as the dashboard's Send-by-File picker.
              Default 'all' keeps legacy behavior; 'file' lazy-loads the
              uploads list (server-side count() per file in this run) and
              persists picked names as sourceFiles on the schedule doc.
              Each fire then dispatches only from those files; if they don't
              have enough eligible leads, fewer calls are sent (same caveat
              as the dashboard send). */}
          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Lead source *</label>
            {/* Two stacked full-width rows so this control matches the column
                width of Time/Run/Count above. Each row is a bordered "card"
                (same border + bg as inputs) that's clickable end-to-end.
                width:auto on the radio is defense-in-depth against the global
                `.form-group input { width: 100% }` rule that would otherwise
                stretch radios and break the label flow — same gotcha called
                out in the acknowledgment row below. */}
            <div style={{ display: 'grid', gap: '8px', marginBottom: '8px' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 14px', borderRadius: '6px',
                border: `1px solid ${scopeMode === 'all' ? 'var(--gold)' : 'var(--dark-3)'}`,
                background: scopeMode === 'all' ? 'rgba(221,177,84,0.08)' : 'var(--dark)',
                cursor: 'pointer', fontSize: '1rem',
              }}>
                <input
                  type="radio" checked={scopeMode === 'all'}
                  onChange={() => setScopeMode('all')}
                  style={{ width: 'auto', transform: 'scale(1.3)', cursor: 'pointer', flexShrink: 0 }}
                />
                <span>Send all eligible leads</span>
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 14px', borderRadius: '6px',
                border: `1px solid ${scopeMode === 'file' ? 'var(--gold)' : 'var(--dark-3)'}`,
                background: scopeMode === 'file' ? 'rgba(221,177,84,0.08)' : 'var(--dark)',
                cursor: 'pointer', fontSize: '1rem',
              }}>
                <input
                  type="radio" checked={scopeMode === 'file'}
                  onChange={() => setScopeMode('file')}
                  style={{ width: 'auto', transform: 'scale(1.3)', cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>Send by file</span>
                {scopeMode === 'file' && pickedFiles.size > 0 && (
                  <span style={{ color: 'var(--gold)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {pickedFiles.size} selected
                  </span>
                )}
              </label>
            </div>

            {scopeMode === 'file' && (
              <div style={{
                border: '1px solid var(--dark-3)', borderRadius: '8px',
                padding: '12px', background: 'var(--dark)',
              }}>
                {filesLoading && (
                  <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', margin: 0 }}>Loading uploads…</p>
                )}
                {!filesLoading && filesError && (
                  <p style={{ color: 'var(--red)', fontSize: '0.85rem', margin: 0 }}>{filesError}</p>
                )}
                {!filesLoading && fileOptions && fileOptions.length === 0 && !filesError && (
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', margin: 0 }}>
                    No labelled uploads with pending leads in Run {runNumber}.
                  </p>
                )}
                {!filesLoading && fileOptions && fileOptions.length > 0 && (
                  <>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      marginBottom: '8px', fontSize: '0.75rem', alignItems: 'center',
                    }}>
                      <div style={{ color: 'var(--gray-500)' }}>
                        Pick up to {MAX_FILES_PER_SCHEDULE} files. Counts are for Run {runNumber}.
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" onClick={handleSelectAllFiles}
                          className="btn btn-secondary"
                          style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
                          Select all
                        </button>
                        <button type="button" onClick={handleClearFiles}
                          className="btn btn-secondary"
                          style={{ padding: '2px 8px', fontSize: '0.7rem' }}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <div style={{
                      maxHeight: '260px', overflowY: 'auto',
                      border: '1px solid var(--dark-3)', borderRadius: '6px',
                      padding: '4px', background: 'var(--dark-2)',
                    }}>
                      {fileOptions.map(opt => {
                        const isPicked = pickedFiles.has(opt.sourceFile);
                        const noneAvailable = opt.availableInRun === 0;
                        const overCap = pickedFiles.size >= MAX_FILES_PER_SCHEDULE;
                        const disabled = noneAvailable || (!isPicked && overCap);
                        return (
                          <label
                            key={opt.sourceFile}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '8px 10px', borderRadius: '4px',
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              opacity: disabled ? 0.5 : 1,
                              background: isPicked ? 'rgba(200,168,78,0.08)' : 'transparent',
                            }}
                            title={noneAvailable
                              ? `No leads ready in Run ${runNumber} from this file right now — but the schedule fires later, so by then more may be ready. Pick another file or switch back to “Send all eligible leads”.`
                              : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={isPicked}
                              disabled={disabled}
                              onChange={() => togglePicked(opt.sourceFile, !noneAvailable)}
                              style={{ width: 'auto' }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  color: 'var(--white)', fontSize: '0.85rem',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}
                                title={opt.sourceFile}
                              >
                                {opt.sourceFile}
                              </div>
                              <div style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                                {opt.availableInRun.toLocaleString()} ready
                                {' / '}
                                {opt.totalInRun.toLocaleString()} in Run {runNumber}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {pickedFiles.size >= MAX_FILES_PER_SCHEDULE && (
                      <p style={{ color: 'var(--gold)', fontSize: '0.75rem', marginTop: '8px' }}>
                        Reached the {MAX_FILES_PER_SCHEDULE}-file cap — uncheck one to swap.
                      </p>
                    )}
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.7rem', marginTop: '8px', lineHeight: 1.4 }}>
                      Each fire pulls only from selected files. If they don't have enough eligible leads at fire time, fewer calls are sent — the gap is not filled from other files.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ fontSize: '1rem' }}>Estimated cost per fire</label>
            <div style={{ padding: '12px 14px', borderRadius: '6px', background: 'var(--dark)',
              border: '1px solid var(--dark-3)', color: 'var(--gray-300)', fontSize: '1rem' }}>
              ~{estimatedCostPerFire} credits per fire
              {mode === 'weekly' && ` · ~${estimatedRecurringCost} credits/week (current balance: ${activeBalance})`}
            </div>
          </div>

          {/* Acknowledgment row — two side-by-side modules. Wrapped in a plain
              div (NOT .form-group) so the global `.form-group input { width:
              100% }` rule in index.css doesn't stretch the checkbox to full
              width. We also explicitly set width:auto on the checkbox as
              defense-in-depth in case any future global rule reaches in. */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'stretch' }}>
            <div
              onClick={() => setAcknowledged(!acknowledged)}
              style={{
                flexShrink: 0, padding: '0 22px',
                borderRadius: '6px', background: 'var(--dark)',
                border: '1px solid var(--dark-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox" checked={acknowledged}
                onChange={e => setAcknowledged(e.target.checked)}
                onClick={e => e.stopPropagation()}
                style={{
                  width: 'auto', transform: 'scale(1.6)', cursor: 'pointer',
                }}
              />
            </div>
            <div
              onClick={() => setAcknowledged(!acknowledged)}
              style={{
                flex: 1, padding: '14px 16px', borderRadius: '6px',
                background: 'var(--dark)', border: '1px solid var(--dark-3)',
                cursor: 'pointer', fontSize: '1rem', lineHeight: 1.5,
                display: 'flex', alignItems: 'center',
              }}
            >
              {consentText}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="submit" className="btn btn-primary"
              disabled={submitting || !acknowledged}>
              {submitting ? 'Creating...' : `Schedule ${mode === 'weekly' ? 'Weekly' : 'One-Time'}`}
            </button>
            <Link to="/" className="btn btn-secondary">Cancel</Link>
          </div>
        </form>

        {result && (
          <div style={{
            marginTop: '24px', padding: '16px', borderRadius: '8px',
            background: result.startsWith('Error') ? 'var(--red-bg)' : 'var(--green-bg)',
            color: result.startsWith('Error') ? 'var(--red)' : 'var(--green)',
            border: `1px solid ${result.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`
          }}>{result}</div>
        )}
      </div>

      {/* Acknowledgment modal */}
      {showAckModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: 'var(--dark)', border: '1px solid var(--dark-3)',
            borderRadius: '8px', padding: '24px', maxWidth: '500px',
          }}>
            <h3>Confirm Scheduled Run</h3>
            <p style={{ marginTop: '12px' }}>{consentText}</p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button type="button" className="btn btn-primary" onClick={submit}>I acknowledge — schedule it</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAckModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient-credits modal */}
      {showCreditModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: 'var(--dark)', border: '1px solid var(--red)',
            borderRadius: '8px', padding: '24px', maxWidth: '480px',
          }}>
            <h3 style={{ color: 'var(--red)' }}>Insufficient credits</h3>
            <p style={{ marginTop: '12px' }}>
              This schedule needs <strong>{estimatedRecurringCost}</strong> credits per
              {mode === 'weekly' ? ' week' : ' fire'}. You currently have <strong>{activeBalance}</strong>.
              Buy at least <strong>{neededCredits}</strong> more to schedule this.
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button type="button" className="btn btn-primary"
                onClick={() => navigate('/buy-credits?suggested=' + neededCredits)}>
                Buy {neededCredits} credits
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreditModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
